import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import { addDays } from "@/core/format/format";

import { PLAN_LIMITS, type PlanStatus } from "./subscription-display";

export const PLAN_PAGE_SIZE = 25;

export interface PlanFilters {
  search?: string;
  status?: PlanStatus;
  /** One customer's plans. */
  userId?: string;
}

export function useCustomerPlans(brandId: string, filters: PlanFilters) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.customers.list(brandId, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(
        api.GET(ENDPOINTS.customerPlans, {
          params: {
            path: { brandId },
            query: {
              limit: PLAN_PAGE_SIZE,
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.userId ? { userId: filters.userId } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
  });
}

/**
 * One plan with the meals of one window: `from` and the following days (the API allows at most 62 days per
 * call). The previous window stays on screen while the next one loads, so nothing jumps.
 */
export function useCustomerPlan(brandId: string, subscriptionId: string, from?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.customers.detail(brandId, subscriptionId, from),
    queryFn: async () =>
      (
        await call(
          api.GET(ENDPOINTS.customerPlan, {
            params: {
              path: { brandId, subscriptionId },
              query: from ? { from, to: addDays(from, PLAN_LIMITS.mealRangeDays - 1) } : {},
            },
          }),
        )
      ).data,
    placeholderData: (previous) => previous,
  });
}

export function useAddBonusMeals(brandId: string, subscriptionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { count: number; slotId: string; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.customerPlanBonusMeals, {
            params: { path: { brandId, subscriptionId } },
            body: input,
          }),
        )
      ).data,
    // The plan, the customer list (meals left) and the home numbers all change.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brand(brandId) }),
  });
}

/** Takes open meals off the end of the plan (only when a Brocoly setting allows the brand team). */
export function useRemoveMeals(brandId: string, subscriptionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { count: number; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.customerPlanRemoveMeals, {
            params: { path: { brandId, subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brand(brandId) }),
  });
}

/** Makes the plan have exactly this many meals left (removes from the end, or adds free meals). */
export function useSetMealsLeft(brandId: string, subscriptionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { mealsLeft: number; reason: string }) =>
      (
        await call(
          api.PUT(ENDPOINTS.customerPlanMealsLeft, {
            params: { path: { brandId, subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brand(brandId) }),
  });
}

/** Moves the customer to another plan of the brand (meals left carry over; dish choices are cleared). */
export function useMovePlan(brandId: string, subscriptionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { planId: string; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.customerPlanMovePlan, {
            params: { path: { brandId, subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brand(brandId) }),
  });
}
